import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { formatGetal, isTelling } from '../format';
import type { WerksoortSleutel } from '../work-quality.api';

/** Eén veld dat geen kaart kreeg: met aantal, of (te weinig gegevens) alleen met naam. */
export interface OverigVeld {
  veld: string;
  naam: string;
  aantal?: number;
}

/** Wat "Meer over deze week" van één werksoort met cijfer laat zien. */
export interface MeerOverWerksoort {
  sleutel: WerksoortSleutel;
  naam: string;
  overig: OverigVeld[];
  aantalMetCorrectie?: number;
  aantalBeoordeeld: number;
}

export interface MeerOverDezeWeekProps {
  werksoorten: MeerOverWerksoort[];
  kopNiveau: 2 | 3 | 4;
}

const TEKEN_OPEN = '▸';

/**
 * "Meer over deze week" (EXPERIENCE.md): een knop met `aria-expanded` in een kop, standaard dicht. Daarin per
 * werksoort met cijfer de velden die geen Aandachtskaart kregen, de telling van producten met een aanpassing,
 * en daarna "Hoe lees je dit?". Geen animatie; dicht is `hidden`, zodat niets ervan voorgelezen wordt.
 */
export function MeerOverDezeWeek({ werksoorten, kopNiveau }: MeerOverDezeWeekProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [open, setOpen] = useState(false);
  const inhoudId = useId();
  const Kop = `h${kopNiveau}` as const;
  const Subkop = `h${(kopNiveau + 1) as 3 | 4 | 5}` as const;

  return (
    <section className="rounded-[12px] border border-[var(--border-soft)] bg-[var(--background)]">
      <Kop className="text-[0.9375rem] font-semibold text-[var(--foreground-heading)]">
        <button
          aria-controls={inhoudId}
          aria-expanded={open}
          className={`flex min-h-[2.75rem] w-full items-center gap-2 rounded-[12px] px-5 py-3 text-left ${FOCUSRING}`}
          onClick={() => setOpen(!open)}
          type="button"
        >
          <span aria-hidden="true" className={`inline-block text-[var(--foreground-muted)] ${open ? 'rotate-90' : ''}`}>
            {TEKEN_OPEN}
          </span>
          <span>{t('qa.rapportkaart.meerOverKop')}</span>
        </button>
      </Kop>
      <div className="flex flex-col gap-5 px-5 pb-5" data-testid="werkkwaliteit-meer-over-inhoud" hidden={!open} id={inhoudId}>
        {werksoorten.map((w) => (
          <div className="flex flex-col gap-2" key={w.sleutel}>
            <Subkop className="text-[0.8125rem] font-semibold text-[var(--foreground-heading)]">{w.naam}</Subkop>
            {w.overig.length > 0 && (
              <>
                <p className="text-[0.875rem] text-[var(--foreground)]">{t('qa.rapportkaart.andereVelden')}</p>
                <ul className="flex list-disc flex-col gap-1 pl-5 text-[0.875rem] text-[var(--foreground)]">
                  {w.overig.map((veld) => (
                    <li className="break-words" key={veld.veld}>
                      {veld.aantal === undefined
                        ? veld.naam
                        : t('qa.rapportkaart.veldMetAantal', { naam: veld.naam, aantal: formatGetal(veld.aantal, locale) })}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {isTelling(w.aantalMetCorrectie) && (
              <p className="text-[0.875rem] text-[var(--foreground)]">
                {t('qa.rapportkaart.aanpassingen', {
                  aantal: formatGetal(w.aantalMetCorrectie, locale),
                  totaal: formatGetal(w.aantalBeoordeeld, locale),
                })}
              </p>
            )}
          </div>
        ))}
        <div className="rounded-[12px] border border-[var(--border)] bg-[var(--background-muted)] p-4" data-testid="werkkwaliteit-hoe-lees-je-dit">
          <Subkop className="text-[0.8125rem] font-semibold text-[var(--foreground-heading)]">{t('qa.eigen.hoeLeesJeDitKop')}</Subkop>
          <p className="mt-1 text-[0.8125rem] text-[var(--foreground)]">{t('qa.eigen.hoeLeesJeDitTekst')}</p>
        </div>
      </div>
    </section>
  );
}
